/**
 * Display-field configuration and staging helpers for the "Link to Source"
 * modal (issue #622, Task 9 — docs/features/external-source-linking.md,
 * sub-tasks 9.2/9.4).
 *
 * Field lists below mirror the SAME fields OrangeBookPanel/NDCPanel/DMFPanel
 * already show for a LINKED record (issue #622 Task 8), applied here to a
 * search result / staged-for-review record before it's linked — reusing
 * Task 6.1's `displayColumns` (api/sql/externalLinkSources.js) exactly as
 * Task 7's search endpoint already projects them, never inventing a field
 * the API doesn't return.
 */

import {
  ExternalLinkSearchResult,
  ExternalLinkSource,
  StagedExternalLinkRecord,
} from '../../../services/api/domains/products/types';
import { formatDateTime } from '../../../utils/dateConversion';
import { formatDmfType, formatDmfStatus } from '../../../utils/dmfTypeLabels';
import { parseNdcActiveIngredientNames } from '../../../utils/ndcActiveIngredients';
import { formatDeaSchedule } from '../../../utils/deaScheduleLabels';

export interface ExternalLinkDisplayField {
  /** A key on the source's search-result shape (see types.ts's OBSearchResult/NDCSearchResult/DMFSearchResult). */
  key: string;
  label: string;
  /** True for the handful of fields shown in the compact search-result row; every field (summary or not) appears in the review list's full "view details". */
  summary?: boolean;
  format?: (value: unknown) => string;
}

function formatText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function formatDateField(value: unknown): string {
  return formatDateTime(value) || '—';
}

function formatFinished(value: unknown): string {
  if (value === null || value === undefined) return '—';
  return value ? 'Finished' : 'Unfinished';
}

function formatDmfTypeField(value: unknown): string {
  return formatDmfType(typeof value === 'string' ? value : null);
}

function formatDmfStatusField(value: unknown): string {
  return formatDmfStatus(typeof value === 'string' ? value : null);
}

function formatDeaScheduleField(value: unknown): string {
  return formatDeaSchedule(typeof value === 'string' ? value : null);
}

/**
 * `dmf_number` renders through this instead of the default `formatText` so
 * `UNKNOWN_DMF_NUMBER` (see the sentinel's own doc comment below) shows the
 * same "—" unknown-value convention as every other missing field, rather
 * than the literal string "NaN" `formatText` would otherwise produce (its
 * null/undefined/'' check doesn't catch `NaN`).
 */
function formatDmfNumberField(value: unknown): string {
  if (typeof value === 'number' && Number.isNaN(value)) return '—';
  return formatText(value);
}

/**
 * Formats the raw `active_ingredients` JSON array string as a comma-joined
 * display string, via the shared `ndcActiveIngredients.ts` parser (2026-09-23
 * follow-up feedback -- this used to re-implement the same JSON parse
 * locally, duplicating a util that was extracted from NDCPanel.tsx
 * specifically so other surfaces wouldn't have to).
 */
function formatActiveIngredients(value: unknown): string {
  if (typeof value !== 'string' || !value) return '—';
  const names = parseNdcActiveIngredientNames(value);
  return names.length > 0 ? names.join(', ') : '—';
}

const ORANGE_BOOK_FIELDS: ExternalLinkDisplayField[] = [
  { key: 'trade_name', label: 'Trade Name', summary: true },
  { key: 'ingredient', label: 'Ingredient', summary: true },
  { key: 'appl_type', label: 'Application Type' },
  { key: 'appl_no', label: 'Application No.' },
  { key: 'product_no', label: 'Product No.' },
  { key: 'approval_date', label: 'Approval Date', format: formatDateField },
  { key: 'te_code', label: 'TE Code' },
  { key: 'strength', label: 'Strength' },
  { key: 'df', label: 'Dosage Form' },
  { key: 'route', label: 'Route' },
  { key: 'applicant_full_name', label: 'Applicant' },
  // Rendered as JSX (ObTypeValue), same as te_code/appl_type above -- no
  // `format` needed here (ExternalLinkReviewList.tsx special-cases this key).
  { key: 'type', label: 'Listing Status' },
  { key: 'data_file_date', label: 'Data File Date', format: formatDateField },
];

const NDC_FIELDS: ExternalLinkDisplayField[] = [
  { key: 'generic_name', label: 'Generic Name', summary: true },
  { key: 'product_ndc', label: 'NDC', summary: true },
  {
    key: 'active_ingredients',
    label: 'Active Ingredients',
    format: formatActiveIngredients,
  },
  { key: 'labeler_name', label: 'Labeler' },
  { key: 'product_type', label: 'Product Type' },
  {
    key: 'finished',
    label: 'Finished/Unfinished',
    summary: true,
    format: formatFinished,
  },
  {
    key: 'dea_schedule',
    label: 'DEA Schedule',
    format: formatDeaScheduleField,
  },
  {
    key: 'marketing_start_date',
    label: 'Marketing Start',
    format: formatDateField,
  },
  {
    key: 'marketing_end_date',
    label: 'Marketing End',
    format: formatDateField,
  },
  {
    key: 'listing_expiration_date',
    label: 'Listing Expiration',
    format: formatDateField,
  },
  { key: 'data_file_date', label: 'Data File Date', format: formatDateField },
];

const DMF_FIELDS: ExternalLinkDisplayField[] = [
  { key: 'subject', label: 'Subject', summary: true },
  {
    key: 'dmf_number',
    label: 'DMF Number',
    summary: true,
    format: formatDmfNumberField,
  },
  { key: 'dmf_type', label: 'Type', format: formatDmfTypeField },
  { key: 'status', label: 'Status', format: formatDmfStatusField },
  { key: 'holder', label: 'Holder' },
  { key: 'submit_date', label: 'Submit Date', format: formatDateField },
  { key: 'data_file_date', label: 'Data File Date', format: formatDateField },
];

/** Full per-source field list -- keyed so callers never hard-code which array belongs to which source. */
export const EXTERNAL_LINK_DISPLAY_FIELDS: Record<
  ExternalLinkSource,
  ExternalLinkDisplayField[]
> = {
  orange_book: ORANGE_BOOK_FIELDS,
  ndc: NDC_FIELDS,
  dmf: DMF_FIELDS,
};

/** Just the fields flagged `summary: true` -- what the compact search-result row shows. */
export function getSummaryFields(
  source: ExternalLinkSource
): ExternalLinkDisplayField[] {
  return EXTERNAL_LINK_DISPLAY_FIELDS[source].filter(f => f.summary);
}

/**
 * Resolves one display field's RAW, unformatted value from a search result,
 * keyed dynamically by `field.key` -- the same dynamic-key-on-a-typed-object
 * pattern already used elsewhere in this codebase for cross-shape access
 * (e.g. `useEditableModal.ts`'s `deepEqual`, which indexes a generic `T` the
 * same way). `record` here is a concrete discriminated union rather than an
 * unconstrained generic, so TypeScript requires the explicit `unknown` hop
 * before `Record<string, unknown>` (neither `OBSearchResult`,
 * `NDCSearchResult`, nor `DMFSearchResult` declares an index signature) --
 * this is that single, TS-mandated hop, not an unrelated second cast forcing
 * an incompatible type.
 *
 * Needed on its own (not just via `resolveExternalLinkFieldValue` below) for
 * `te_code` (issue #805), which renders as JSX (`TeCodeValue`) rather than a
 * plain formatted string.
 */
export function resolveExternalLinkFieldRawValue(
  record: ExternalLinkSearchResult,
  field: ExternalLinkDisplayField
): unknown {
  const bag = record as unknown as Record<string, unknown>;
  return bag[field.key];
}

/** Resolves one display field's value from a search result as a formatted string. */
export function resolveExternalLinkFieldValue(
  record: ExternalLinkSearchResult,
  field: ExternalLinkDisplayField
): string {
  const raw = resolveExternalLinkFieldRawValue(record, field);
  return field.format ? field.format(raw) : formatText(raw);
}

/**
 * `(source, record_id)` dedupe key -- Sub-task 9.4's load-bearing dedupe,
 * designed so a future Sub-task 9.3 "add from suggestions" call site
 * collapses onto the SAME key as a search-added record for the identical
 * underlying row, rather than double-staging it.
 */
export function getStagedRecordKey(record: {
  source: ExternalLinkSource;
  record_id: number;
}): string {
  return `${record.source}:${record.record_id}`;
}

export function toStagedExternalLinkRecord(
  record: ExternalLinkSearchResult,
  addedVia: StagedExternalLinkRecord['addedVia']
): StagedExternalLinkRecord {
  return {
    source: record.source,
    record_id: record.record_id,
    record,
    addedVia,
  };
}

// ---------------------------------------------------------------------------
// Suggestion -> staged record (Sub-task 9.3/9.4).
//
// `GET /api/external-link-search/:productId/suggestions/:source` (Task 7.2)
// used to return only `matched_field`/`matched_variant`/`display_name`/
// `score`/`record_count`/`record_ids` per candidate GROUP — never the full
// per-source display-field set `ExternalLinkSearchResult` carries
// (trade_name, appl_no, generic_name, labeler_name, etc), so a suggestion-
// added staged record could only ever show the one field the response gave
// it. Fixed (issue #622 follow-up) by having the backend's
// `fetchSuggestionCandidatePool`/`scoreCandidatesAgainstVariant`
// (api/sql/externalLinkSearch.js) return a `records` array per group — one
// entry per `record_ids` member, carrying that source's real
// `displayColumns` detail, the exact same field names/shapes the search
// endpoint already returns. This function now just looks up the matching
// entry and merges it into the discriminated `ExternalLinkSearchResult`
// shape — no fabricated/null placeholder fields.
// ---------------------------------------------------------------------------

/**
 * Builds the real `ExternalLinkSearchResult` for one `record_id` inside a
 * suggestion group, from that group's `records` detail (issue #622
 * follow-up). Never called with a `record_id` that isn't a member of the
 * group's own `record_ids` — callers (see
 * `toStagedExternalLinkRecordsFromSuggestionGroup` below) are expected to
 * fan this out over every id in the group, since one suggestion group can
 * represent more than one underlying source record (`record_count > 1`).
 */
export function toExternalLinkSearchResultFromSuggestion(
  source: ExternalLinkSource,
  recordId: number,
  matchedField: string,
  displayName: string,
  records: Array<{ record_id: number } & Record<string, unknown>>
): ExternalLinkSearchResult {
  const detail = records.find(r => r.record_id === recordId);
  if (!detail) {
    // Per this function's own contract (see JSDoc above), every recordId
    // passed in must be a member of `records` — the API's `records` array
    // is documented as one entry per `record_ids`. Reaching here means that
    // contract broke server-side; failing fast surfaces the bug instead of
    // silently rendering a placeholder row with every display field blank
    // (issue #622 review follow-up, PR #719).
    throw new Error(
      `toExternalLinkSearchResultFromSuggestion: no records[] entry for record_id ${recordId} (source: ${source})`
    );
  }
  const base = {
    ...detail,
    record_id: recordId,
    matched_field: matchedField,
    matched_value: displayName,
    // Suggestions exclude already-linked records before scoring
    // (fetchSuggestionCandidatePool's buildLinkedExclusionSql) — a record
    // reaching here is never already linked.
    is_linked: false,
  };

  return { ...base, source } as ExternalLinkSearchResult;
}

/**
 * Fans one suggestion group (one "Candidate Match" row, possibly grouping
 * several underlying records — `record_count > 1`) out into one
 * `StagedExternalLinkRecord` PER `record_id`, `addedVia: 'suggestion'` —
 * Sub-task 9.4's "resolves grouped name selections to specific records
 * here" requirement. Each result dedupes against a search-added record for
 * the same `(source, record_id)` via the identical `getStagedRecordKey`,
 * same as every other staged-record call site.
 */
export function toStagedExternalLinkRecordsFromSuggestionGroup(
  source: ExternalLinkSource,
  group: {
    matched_field: string;
    display_name: string;
    record_ids: number[];
    records: Array<{ record_id: number } & Record<string, unknown>>;
  }
): StagedExternalLinkRecord[] {
  return group.record_ids.map(recordId =>
    toStagedExternalLinkRecord(
      toExternalLinkSearchResultFromSuggestion(
        source,
        recordId,
        group.matched_field,
        group.display_name,
        group.records
      ),
      'suggestion'
    )
  );
}
